/**
 * BatchNoticeModal — multi-exchange subpoena workflow.
 * Select N exchange targets → one notice per exchange → partial-failure + retry.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X, FileText, Loader2, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { legalService, type BatchNoticeResultItem } from '../services/api';
import { collectBatchNoticeTargets, type BatchNoticeTarget } from '../utils/batchNoticeTargets';
import { safeStr } from '../utils/safeStr';
import { useCaseEditBlock } from '../case-canvas/caseAccess';

interface BatchNoticeModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedNodes: any[];
    caseId?: string;
    investigationId?: string;
    initialCaseContext?: {
        firNo?: string;
        firDate?: string;
        sections?: string;
        officerName?: string;
        designation?: string;
        policeStation?: string;
        officerEmail?: string;
    };
}

function downloadBase64Pdf(b64: string, filename: string) {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'notice.pdf';
    a.click();
    URL.revokeObjectURL(url);
}

export const BatchNoticeModal: React.FC<BatchNoticeModalProps> = ({
    isOpen,
    onClose,
    selectedNodes,
    caseId,
    investigationId,
    initialCaseContext,
}) => {
    const collected = useMemo(
        () => collectBatchNoticeTargets(selectedNodes),
        [selectedNodes],
    );
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);
    // Batch notices on a case are stored as case evidence: a viewer collaborator may not generate them (ACC03).
    const { blocked: caseBlocked, reason: caseBlockedReason } = useCaseEditBlock(isOpen ? caseId : undefined);
    const [results, setResults] = useState<BatchNoticeResultItem[] | null>(null);
    const [batchId, setBatchId] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        firNo: '',
        firDate: '',
        sections: '419/420 IPC, 66D IT Act',
        officerName: '',
        designation: 'Inspector of Police',
        policeStation: '',
        officerEmail: '',
    });

    useEffect(() => {
        if (!isOpen) return;
        setResults(null);
        setBatchId(null);
        setSelected(new Set(collected.targets.map(t => t.exchange_name.toLowerCase())));
        if (initialCaseContext) {
            setFormData(prev => ({
                firNo: prev.firNo || initialCaseContext.firNo || '',
                firDate: prev.firDate || initialCaseContext.firDate || '',
                sections: prev.sections || initialCaseContext.sections || prev.sections,
                officerName: prev.officerName || initialCaseContext.officerName || '',
                designation: prev.designation || initialCaseContext.designation || prev.designation,
                policeStation: prev.policeStation || initialCaseContext.policeStation || '',
                officerEmail: prev.officerEmail || initialCaseContext.officerEmail || '',
            }));
        }
    }, [isOpen, collected.targets, initialCaseContext]);

    if (!isOpen) return null;

    const toggle = (name: string) => {
        const key = name.toLowerCase();
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const selectedTargets = collected.targets.filter(t =>
        selected.has(t.exchange_name.toLowerCase()),
    );

    const caseContext = {
        fir_no: formData.firNo,
        fir_date: formData.firDate,
        sections: formData.sections,
        officer_name: formData.officerName,
        designation: formData.designation,
        police_station: formData.policeStation,
        officer_email: formData.officerEmail,
    };

    const runBatch = async (targets: BatchNoticeTarget[]) => {
        if (!targets.length) {
            toast.error('Select at least one exchange');
            return;
        }
        setLoading(true);
        try {
            const res = await legalService.batchGenerateNotices({
                notices: targets.map(t => ({
                    exchange_name: t.exchange_name,
                    chain: t.chain,
                    address: t.address,
                    wallet_role: t.wallet_role,
                    attribution_method: t.attribution_method,
                    attribution_tier: t.attribution_tier,
                    is_offramp_point: t.is_offramp_point ?? true,
                    related_addresses: (t as any).related_addresses,
                })),
                case_context: caseContext,
                case_id: caseId,
                investigation_id: investigationId,
                omit_pdf_bytes: false,
            });
            setResults(res.results);
            setBatchId(res.batch_id);
            const ok = res.summary.success || 0;
            const fail = res.summary.failed || 0;
            const skip = (res.summary.skipped_duplicate || 0) + (res.summary.skipped || 0);
            toast.success(`Batch notices: ${ok} ok, ${fail} failed, ${skip} skipped`);
            for (const r of res.results) {
                if (r.status === 'success' && r.pdf_base64) {
                    downloadBase64Pdf(r.pdf_base64, r.filename || `Notice_${r.exchange_name}.pdf`);
                }
            }
        } catch (err: any) {
            toast.error(err?.message || 'Batch notice generation failed');
        } finally {
            setLoading(false);
        }
    };

    const failedTargets = (results || [])
        .filter(r => r.status === 'failed' && r.retryable !== false)
        .map(r => collected.targets.find(t =>
            t.exchange_name.toLowerCase() === (r.exchange_name || '').toLowerCase(),
        ))
        .filter(Boolean) as BatchNoticeTarget[];

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" data-testid="batch-notice-modal">
            <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <div>
                        <div className="text-sm font-semibold flex items-center gap-2">
                            <FileText className="w-4 h-4" /> Batch Subpoena Notices
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                            One notice per exchange · duplicates collapsed
                            {collected.duplicatesCollapsed > 0 && ` (${collected.duplicatesCollapsed})`}
                        </div>
                    </div>
                    <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-accent">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                    {collected.targets.length === 0 ? (
                        <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                            No exchange / off-ramp targets found in the selection. Select exchange
                            wallets or addresses with off-ramp attribution.
                        </div>
                    ) : (
                        <div className="space-y-1.5">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                                Exchanges ({selectedTargets.length}/{collected.targets.length})
                            </div>
                            {collected.targets.map(t => {
                                const key = t.exchange_name.toLowerCase();
                                const checked = selected.has(key);
                                return (
                                    <label
                                        key={key}
                                        className="flex items-start gap-2.5 px-2.5 py-2 rounded-md border border-border hover:bg-accent/40 cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggle(t.exchange_name)}
                                            className="mt-0.5"
                                        />
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium">{safeStr(t.exchange_name)}</div>
                                            <div className="text-[10px] text-muted-foreground font-mono truncate">
                                                {t.address} · {t.chain} · {t.source}
                                            </div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-2">
                        {[
                            { name: 'firNo', label: 'FIR No' },
                            { name: 'firDate', label: 'FIR Date' },
                            { name: 'officerName', label: 'Officer' },
                            { name: 'policeStation', label: 'Police Station' },
                        ].map(f => (
                            <label key={f.name} className="text-[10px] text-muted-foreground">
                                {f.label}
                                <input
                                    name={f.name}
                                    value={(formData as any)[f.name]}
                                    onChange={e => setFormData(prev => ({ ...prev, [f.name]: e.target.value }))}
                                    className="mt-0.5 w-full text-xs px-2 py-1.5 rounded border border-border bg-background"
                                />
                            </label>
                        ))}
                    </div>

                    {results && (
                        <div className="space-y-1.5 border-t border-border pt-3">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                                Results {batchId ? `· batch ${batchId.slice(0, 8)}` : ''}
                            </div>
                            {results.map((r, i) => (
                                <div
                                    key={`${r.exchange_name}-${i}`}
                                    className="flex items-start gap-2 text-xs px-2 py-1.5 rounded border border-border"
                                >
                                    {r.status === 'success' ? (
                                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0 mt-0.5" />
                                    ) : r.status === 'failed' ? (
                                        <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
                                    ) : (
                                        <span className="w-3.5 h-3.5 text-muted-foreground shrink-0">–</span>
                                    )}
                                    <div className="min-w-0">
                                        <div className="font-medium">{r.exchange_name || '—'} · {r.status}</div>
                                        {r.error && <div className="text-red-600 dark:text-red-400">{r.error}</div>}
                                        {r.evidence_id != null && (
                                            <div className="text-muted-foreground">evidence #{r.evidence_id}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="px-4 py-3 border-t border-border flex items-center justify-end gap-2">
                    {failedTargets.length > 0 && (
                        <button
                            type="button"
                            disabled={loading}
                            onClick={() => runBatch(failedTargets)}
                            className="px-3 py-1.5 text-xs rounded-md border border-amber-500/40 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10 flex items-center gap-1.5"
                        >
                            <RefreshCw className="w-3.5 h-3.5" /> Retry failed ({failedTargets.length})
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-3 py-1.5 text-xs rounded-md border border-border hover:bg-accent"
                    >
                        Close
                    </button>
                    <button
                        type="button"
                        disabled={loading || selectedTargets.length === 0 || caseBlocked}
                        title={caseBlocked ? caseBlockedReason : undefined}
                        onClick={() => runBatch(selectedTargets)}
                        data-testid="batch-notice-generate"
                        className="px-3 py-1.5 text-xs rounded-md bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-40 flex items-center gap-1.5"
                    >
                        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                        Generate {selectedTargets.length} notice{selectedTargets.length === 1 ? '' : 's'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BatchNoticeModal;
